import {
  Autocomplete,
  Button,
  Divider,
  Flex,
  Grid,
  Label,
  Loader,
  SelectField,
  TextAreaField,
  TextField,
} from "@aws-amplify/ui-react";
import { useContext, useEffect, useState } from "react";
import LocalizedStrings from "react-localization";
import toast from "react-hot-toast";
import { API, graphqlOperation } from "aws-amplify";
import {
  deviceActionsByDeviceID,
  deviceVariablesByDeviceID,
  listActions,
  listCompanies,
  listVariables,
} from "graphql/queries";

import useErrors from "hooks/useErrors";
import DevicesChoose from "components/devices/DevicesChoose";
import LabelRequired from "components/utils/LabelRequired";
import { globalStrings } from "config/translations/es";
import ConditionForm from "components/alerts/ConditionForm";
import PhoneList from "components/phone/PhoneList";
import InputList from "components/utils/InputList";
import { LayoutContext } from "layout/context/LayoutContext";
import { valueTypes } from "config/constants";
import AlertSchedule from "components/alerts/AlertSchedule";

const RuleForm = ({ onSubmit, type = "create", initialValues }) => {
  const { me } = useContext(LayoutContext);
  const [variables, setVariables] = useState([]);
  const [actions, setActions] = useState({
    data: [],
    loading: false,
  });
  const [isLoadingVariables, setIsLoadingVariables] = useState(false);
  const [ruleForm, setRuleForm] = useState(initialValues);
  const [resetValues, setResetValues] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const validateErrors = useErrors({
    name: "isRequired",
    action: "isRequired",
    companyID: "isRequired",
  });
  const validateListErrors = useErrors({
    firstParam: "isRequired",
    operator: "isRequired",
    secondParam: "isRequired",
    time: "isRequired",
    timeValue: "isRequired",
    grouper: "isRequired",
    phone: "isPhone",
    email: "isEmail",
  });

  const onSubmitForm = () => {
    let hasErrors = false;

    Object.entries(ruleForm).forEach((itemForm) => {
      const [name, value] = itemForm;
      const check = !!checkErrors(name, value);
      if (!hasErrors) hasErrors = check;
    });

    if (!companies.some((companyRec) => companyRec.id === ruleForm.companyID)) {
      setErrors((prevSt) => ({
        ...prevSt,
        companyID: strings.validations.incorrectValue,
      }));
      return;
    }

    if (
      (actions.data?.find((act) => act.id === ruleForm.action)?.valueType ===
        valueTypes[1] ||
        actions.data?.find((act) => act.id === ruleForm.action)?.valueType ===
          valueTypes[2]) &&
      !ruleForm.actionValue
    ) {
      toast.error(strings.fields.actionValue.fieldsMissing);
      return;
    }

    if (
      ruleForm.wspNotifications?.some(
        (wsp) => !!validateListErrors("phone", wsp)
      ) ||
      ruleForm.smsNotifications?.some(
        (sms) => !!validateListErrors("phone", sms)
      )
    ) {
      toast.error(strings.wspNotifications.invalid);
      return;
    }

    if (
      ruleForm.emailNotifications?.some(
        (email) => !!validateListErrors("email", email)
      )
    ) {
      toast.error(strings.emailNotifications.invalid);
      return;
    }

    if (
      ruleForm.conditions?.some((cond) =>
        Object.entries(cond).some(
          ([name, value]) => !!validateListErrors(name, value)
        )
      ) ||
      !ruleForm.conditions?.every((cond) =>
        variables.some((variableIt) => variableIt.id === cond.firstParam)
      )
    ) {
      toast.error(strings.conditions.fieldsMissing);
      return;
    }

    if (!hasErrors)
      onSubmit({
        ...ruleForm,
        smsNotifications: ruleForm.smsNotifications?.filter((sms) => !!sms),
        wspNotifications: ruleForm.wspNotifications?.filter((wsp) => !!wsp),
        emailNotifications: ruleForm.emailNotifications?.filter(
          (email) => !!email
        ),
        companyID: ruleForm.companyID || null,
      });
  };

  const onClearSelect = () => {
    const value = "";
    fetchCompanies({ name: value });
    setRuleForm((prevSt) => ({
      ...prevSt,
      companyID: value,
    }));
  };

  const onSelect = (name, value) => {
    setRuleForm((prevSt) => ({
      ...prevSt,
      [name]: value,
    }));
    checkErrors(name, value);
  };

  const onChangeSelect = (e) => {
    const { name, value } = e.target;
    fetchCompanies({ name: value });
    setRuleForm((prevSt) => ({
      ...prevSt,
      [name]: value,
    }));
    checkErrors(name, value);
  };

  const onChange = (e) => {
    let { name, value } = e.target;

    setRuleForm((prevSt) => ({
      ...prevSt,
      [name]: value,
    }));
    checkErrors(name, value);

    if (name === "action") {
      setRuleForm((prevSt) => ({
        ...prevSt,
        actionValue: "",
      }));
    }
  };

  const onChangeSmsNotifications = (list) => {
    setRuleForm((prevSt) => ({
      ...prevSt,
      smsNotifications: list ?? [],
    }));
  };

  const onChangeWspNotifications = (list) => {
    setRuleForm((prevSt) => ({
      ...prevSt,
      wspNotifications: list ?? [],
    }));
  };

  const onChangeEmailNotifications = (list) => {
    setRuleForm((prevSt) => ({
      ...prevSt,
      emailNotifications: list ?? [],
    }));
  };

  const onChangeConditions = (cond) => {
    setRuleForm((prevSt) => ({
      ...prevSt,
      conditions: cond ?? [],
    }));
  };

  const onChangeDevices = (devices) => {
    setRuleForm((prevSt) => ({
      ...prevSt,
      devices,
    }));
  };

  const onChangeSchedule = (name, value) => {
    if (name === "execInterval") {
      if (value === "hour")
        setRuleForm((prevSt) => ({
          ...prevSt,
          execTime: "",
          hourInterval: 1,
        }));
      else
        setRuleForm((prevSt) => ({
          ...prevSt,
          startSchedule: "",
          endSchedule: "",
          hourInterval: null,
        }));
    }
    setRuleForm((prevSt) => ({
      ...prevSt,
      [name]: value,
    }));
  };

  const checkErrors = (name, value) => {
    const errorMessage = validateErrors(name, value);
    if (errorMessage)
      setErrors((prevErr) => ({ ...prevErr, [name]: errorMessage }));
    else setErrors((prevErr) => ({ ...prevErr, [name]: "" }));
    return errorMessage;
  };

  const resetFormValues = () => {
    setErrors({});
    setRuleForm(initialValues);
    setResetValues((prev) => !prev);
  };

  async function fetchCompanies({ id = "", name = "" }) {
    let filterObj = {};
    try {
      setCompaniesLoading(true);

      if (me.companyID)
        filterObj = { ...filterObj, or: [{ id: { eq: me.companyID } }] };
      if (me.managedCompanies.length > 0)
        filterObj = {
          ...filterObj,
          or: [
            ...(filterObj?.or ?? []),
            ...me.managedCompanies.map((cp) => ({ id: { eq: cp } })),
          ],
        };

      const companiesData = await API.graphql(
        graphqlOperation(listCompanies, {
          limit:
            !id && !name && Object.keys(filterObj).length <= 0 ? 20 : undefined,
          filter: {
            and: [
              {
                id: { contains: id },
              },
              {
                name: { contains: name },
              },
            ],
            ...filterObj,
          },
        })
      );
      const companiesItems = companiesData.data.listCompanies.items;
      setCompanies(companiesItems);
      setCompaniesLoading(false);
    } catch (err) {
      toast.error(strings.responses.errorFetchCompanies);
    }
  }

  async function fetchDeviceActions(devices) {
    try {
      setActions((prevSt) => ({
        ...prevSt,
        loading: true,
      }));
      const actionsData = await API.graphql(graphqlOperation(listActions));
      const actionsItems = actionsData.data.listActions.items;

      if (devices?.length > 0) {
        const deviceActionsData = await Promise.all(
          devices?.map((device) =>
            API.graphql(
              graphqlOperation(deviceActionsByDeviceID, {
                deviceID: device.id,
              })
            )
          )
        );

        const actionsByDevice = deviceActionsData?.map(
          (devVarData) =>
            devVarData?.data?.deviceActionsByDeviceID?.items?.map(
              (devVarItem) => devVarItem.actionID
            ) ?? []
        );

        let uniqueActions = [];

        actionsByDevice?.forEach((listActions) => {
          const listGetted = listActions.filter((action) =>
            actionsByDevice.every((listActionsSec) =>
              listActionsSec.some((actionSec) => actionSec === action)
            )
          );
          uniqueActions.push(...listGetted);
        });

        uniqueActions = [...new Set(uniqueActions)];

        if (uniqueActions?.length > 0) {
          setActions((prevSt) => ({
            ...prevSt,
            data: actionsItems?.filter((action) =>
              uniqueActions.some((unique) => action.id === unique)
            ),
          }));
        } else
          setActions((prevSt) => ({
            ...prevSt,
            data: [],
          }));
      } else {
        setActions((prevSt) => ({
          ...prevSt,
          data: actionsItems,
        }));
      }
      setActions((prevSt) => ({
        ...prevSt,
        loading: false,
      }));
    } catch (err) {
      toast.error(strings.responses.errorFetchActions);
    }
  }

  async function fetchDeviceVariables(devices) {
    try {
      setIsLoadingVariables(true);
      const variablesData = await API.graphql(graphqlOperation(listVariables));
      const variablesItems = variablesData.data.listVariables.items;

      if (devices?.length > 0) {
        const deviceVariablesData = await Promise.all(
          devices?.map((device) =>
            API.graphql(
              graphqlOperation(deviceVariablesByDeviceID, {
                deviceID: device.id,
              })
            )
          )
        );

        const variablesByDevice = deviceVariablesData?.map(
          (devVarData) =>
            devVarData?.data?.deviceVariablesByDeviceID?.items?.map(
              (devVarItem) => devVarItem.variableID
            ) ?? []
        );

        let uniqueVariables = [];

        variablesByDevice?.forEach((listVariables) => {
          const listGetted = listVariables.filter((variable) =>
            variablesByDevice.every((listVariablesSec) =>
              listVariablesSec.some((variableSec) => variableSec === variable)
            )
          );
          uniqueVariables.push(...listGetted);
        });

        uniqueVariables = [...new Set(uniqueVariables)];

        if (uniqueVariables?.length > 0) {
          setVariables(
            variablesItems?.filter((variable) =>
              uniqueVariables.some((unique) => variable.id === unique)
            )
          );
        } else setVariables([]);
      } else {
        setVariables(variablesItems);
      }
      setIsLoadingVariables(false);
    } catch (err) {
      toast.error(strings.responses.errorFetchVariables);
    }
  }

  useEffect(() => {
    fetchDeviceVariables(ruleForm.devices);
    fetchDeviceActions(ruleForm.devices);
  }, [ruleForm.devices]);

  useEffect(() => {
    setRuleForm(initialValues);
    fetchCompanies({ id: initialValues.companyID });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues]);

  return (
    <Grid rowGap="15px" columnGap="15px" padding="20px">
      <Grid rowGap="15px" columnGap="15px" templateColumns={{ medium: "1fr" }}>
        <TextField
          name="name"
          label={<LabelRequired text={strings.fields.name.label} />}
          value={ruleForm.name}
          onChange={onChange}
          errorMessage={errors?.name}
          hasError={!!errors?.name}
        ></TextField>
        <TextAreaField
          name="description"
          label={strings.fields.description.label}
          value={ruleForm.description}
          onChange={onChange}
        ></TextAreaField>
      </Grid>
      {type === "create" && (
        <>
          <Label htmlFor="companyID">
            {<LabelRequired text={strings.fields.company.label} />}
          </Label>
          <div style={{ marginTop: "-5px" }}>
            <Autocomplete
              id="companyID"
              name="companyID"
              label={<LabelRequired text={strings.fields.company.label} />}
              isRequired
              placeholder={strings.fields.company.placeholder}
              value={
                companies.find(
                  (companyRec) => companyRec.id === ruleForm.companyID
                )?.name ??
                ruleForm.companyID ??
                ""
              }
              options={companies.map((companyRec) => ({
                id: companyRec.id,
                label: companyRec.name,
              }))}
              isLoading={companiesLoading}
              onClear={() => onClearSelect("companyID")}
              onChange={onChangeSelect}
              onSelect={({ id }) => onSelect("companyID", id)}
              errorMessage={errors?.companyID}
              hasError={!!errors?.companyID}
              menuSlots={{ Empty: globalStrings.noResults }}
            ></Autocomplete>
          </div>
        </>
      )}
      <Divider className="mt-3 mb-2" />
      <AlertSchedule form={ruleForm} onChange={onChangeSchedule} />
      <Divider className="mt-3 mb-2" />
      <DevicesChoose
        initDevicesGranted={initialValues.devices}
        onChangeDevicesGranted={onChangeDevices}
        resetValues={resetValues}
        company={ruleForm.companyID}
      />
      <Grid
        rowGap="15px"
        columnGap="15px"
        templateColumns={{ medium: "1fr 1fr" }}
      >
        {!actions.loading ? (
          <SelectField
            name="action"
            value={ruleForm.action}
            label={<LabelRequired text={strings.fields.action.label} />}
            onChange={onChange}
            errorMessage={errors?.action}
            hasError={!!errors?.action}
          >
            <option value="">{globalStrings.selectOption}</option>
            {actions.data?.map((action) => (
              <option value={action.id}>{action.name}</option>
            ))}
          </SelectField>
        ) : (
          <Loader />
        )}
        {ruleForm.action &&
          actions.data?.find((act) => act.id === ruleForm.action)?.valueType ===
            valueTypes[1] && (
            <SelectField
              name="actionValue"
              value={ruleForm.actionValue}
              label={strings.fields.actionValue.label}
              onChange={onChange}
              errorMessage={errors?.actionValue}
              hasError={!!errors?.actionValue}
            >
              <option value="">{globalStrings.selectOption}</option>
              {JSON.parse(
                actions.data?.find((act) => act.id === ruleForm.action)
                  ?.values ?? "[]"
              )?.map((actVal) => (
                <option value={actVal.value}>{actVal.name}</option>
              ))}
            </SelectField>
          )}
        {ruleForm.action &&
          actions.data?.find((act) => act.id === ruleForm.action)?.valueType ===
            valueTypes[2] && (
            <TextField
              name="actionValue"
              label={strings.fields.actionValue.label}
              value={ruleForm.actionValue}
              type="number"
              onChange={onChange}
            ></TextField>
          )}
      </Grid>
      <Divider className="mt-3 mb-2" />
      <PhoneList
        title={strings.smsNotifications.title}
        values={ruleForm.smsNotifications ?? []}
        onChange={onChangeSmsNotifications}
      />
      <Divider className="mt-1 mb-2" />
      <PhoneList
        title={strings.wspNotifications.title}
        values={ruleForm.wspNotifications ?? []}
        onChange={onChangeWspNotifications}
      />
      <Divider className="mt-1 mb-2" />
      <InputList
        title={strings.emailNotifications.title}
        values={ruleForm.emailNotifications ?? []}
        onChange={onChangeEmailNotifications}
        inputPlaceholder={strings.emailNotifications.inputPlaceholder}
        inputType="email"
      />
      <Divider className="mt-1 mb-2" />
      <ConditionForm
        title={strings.conditions.title}
        values={ruleForm.conditions ?? []}
        onChange={onChangeConditions}
        variables={variables ?? []}
        isLoadingVariables={isLoadingVariables}
      />
      <Flex justifyContent="end">
        <Button
          children={strings.fields.buttons.reset}
          onClick={resetFormValues}
        ></Button>
        <Button
          children={
            type === "create"
              ? strings.fields.buttons.create
              : strings.fields.buttons.save
          }
          onClick={onSubmitForm}
          variation="primary"
          isDisabled={Object.values(errors).some((e) => !!e)}
        ></Button>
      </Flex>
    </Grid>
  );
};

const strings = new LocalizedStrings({
  es: {
    responses: {
      errorFetchVariables: "Error al consultar variables",
      errorFetchActions: "Error al consultar acciones",
      errorFetchCompanies: "Error al consultar empresas",
    },
    fields: {
      name: {
        label: "Nombre",
      },
      description: {
        label: "Descripción",
      },
      startSchedule: {
        label: "Hora de Inicio",
      },
      endSchedule: {
        label: "Hora de Fin",
      },
      days: {
        label: "Día de la Semana",
      },
      action: {
        label: "Acción",
      },
      actionValue: {
        label: "Valor de la acción",
        on: "On",
        off: "Off",
        fieldsMissing: "La acción debe contener un valor",
      },
      executeOnHolidays: {
        label: "Ejecutar alerta en días festivos",
      },
      company: {
        label: "Empresa",
        placeholder: "Buscar empresa...",
      },
      buttons: {
        create: "Crear",
        save: "Guardar",
        reset: "Restablecer",
      },
    },
    selectOption: "Seleccione una opción",
    smsNotifications: {
      title: "Notificaciones por SMS",
    },
    wspNotifications: {
      title: "Notificaciones por Whatsapp",
      invalid: "Teléfonos inválidos",
    },
    emailNotifications: {
      title: "Notificaciones por Correo",
      invalid: "Correos inválidos",
      inputPlaceholder: "Ingrese correo",
    },
    conditions: {
      title: "Condiciones",
      fieldsMissing: "Faltan campos por rellenar en la sección de Condiciones",
      isRequired: "Debe existir al menos 1 condición",
    },
    validations: {
      incorrectValue: "Valor incorrecto",
    },
  },
});

export default RuleForm;
